import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Wrench, Star, MapPin, Phone, CheckCircle, User, Calendar, MessageSquare
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import ClientCalendar from '../components/ClientCalendar';
import NoIndex from '../components/NoIndex';

const API_URL = '';

const SERVICE_NAMES_MAP = {
  plumber: 'services.plumber',
  electrician: 'services.electrician',
  carpenter: 'services.carpenter',
  painter: 'services.painter',
  heating: 'services.heating',
  bricklayer: 'services.bricklayer',
  ceramicist: 'services.ceramicist',
  hvac: 'services.hvac',
  appliance: 'services.appliance',
  locksmith: 'services.locksmith',
  cleaning: 'services.cleaning',
  vulkanizer: 'services.vulkanizer',
  handyman: 'services.handyman',
};

const RepairmanProfile = () => {
  const { repairmanId } = useParams();
  const { t } = useTranslation();
  const [repairman, setRepairman] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchData();
    fetchCurrentUser();
  }, [repairmanId]);

  // After data loads, if URL has #reviews hash, smooth-scroll to the reviews section.
  // Page renders calendar/availability blocks ASYNCHRONOUSLY which shift layout —
  // a single timeout misses the target. We poll the element position for ~2s and
  // re-scroll until it stabilizes.
  useEffect(() => {
    if (isLoading || window.location.hash !== '#reviews') return;

    let attempts = 0;
    let lastTop = -1;
    const tick = () => {
      const el = document.getElementById('reviews');
      if (!el) {
        if (attempts++ < 20) setTimeout(tick, 150);
        return;
      }
      const rect = el.getBoundingClientRect();
      const top = rect.top + window.scrollY;
      // Always re-scroll if target moved (calendar loaded), or first 3 attempts
      if (top !== lastTop || attempts < 3) {
        lastTop = top;
        window.scrollTo({ top: top - 80, behavior: 'smooth' });
      }
      if (attempts++ < 15) setTimeout(tick, 200);
    };
    setTimeout(tick, 50);
  }, [isLoading]);

  const fetchData = async () => {
    try {
      const [repairmanRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/api/repairmen/${repairmanId}`),
        axios.get(`${API_URL}/api/reviews/repairman/${repairmanId}`)
      ]);
      setRepairman(repairmanRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(res.data);
    } catch { /* not logged in */ }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        {/* Prevent Googlebot from indexing the loading skeleton as Soft 404 if
            it arrives before the profile data resolves. */}
        <NoIndex />
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!repairman) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        {/* Deleted / non-existent repairman — mark as noindex so Search Console
            stops reporting these as Soft 404 (32 reported on 2026-07-01). */}
        <NoIndex />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('repairmanProfile.notFound')}</h1>
          <Link to="/" className="text-orange-500 hover:underline">{t('repairmanProfile.backToHome')}</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
                <Wrench className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight text-slate-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                HITNOJE.RS
              </span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="bg-white border border-slate-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-24 h-24 bg-slate-100 flex items-center justify-center flex-shrink-0">
              <User className="w-12 h-12 text-slate-400" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 
                  className="text-3xl font-bold text-slate-900"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid="repairman-name"
                >
                  {repairman.full_name}
                </h1>
                {repairman.is_verified && (
                  <Badge className="bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {t('repairmanProfile.verified')}
                  </Badge>
                )}
                {repairman.verified_prices && (
                  <Badge className="bg-emerald-100 text-emerald-800" data-testid="verified-prices-badge">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Verifikovane cene
                  </Badge>
                )}
              </div>
              
              <div className="flex flex-wrap items-center gap-4 text-slate-600 mb-4">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 text-orange-500 fill-orange-500" />
                  <span className="font-semibold text-slate-900">{repairman.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-slate-400">({repairman.total_reviews || 0} {t('repairmanProfile.reviewsCount')})</span>
                </div>
                {repairman.city && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{repairman.city}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{repairman.total_jobs || 0} {t('repairmanProfile.completedJobs')}</span>
                </div>
              </div>

              {repairman.phone && (
                <a 
                  href={`tel:${repairman.phone}`}
                  className="inline-flex items-center gap-2 text-blue-500 hover:text-blue-600"
                >
                  <Phone className="w-4 h-4" />
                  {repairman.phone}
                </a>
              )}
            </div>

            {repairman.hourly_rate > 0 && (
              <div className="text-right">
                <p className="text-sm text-slate-500">{t('repairmanProfile.hourlyRate')}</p>
                <p className="text-3xl font-bold text-orange-500" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  €{repairman.hourly_rate.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Bio */}
        {repairman.bio && (
          <div className="bg-white border border-slate-200 p-6 mb-6">
            <h2 
              className="text-xl font-semibold text-slate-900 mb-3"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {t('repairmanProfile.aboutTitle')}
            </h2>
            <p className="text-slate-600">{repairman.bio}</p>
          </div>
        )}

        {/* Services */}
        {repairman.services?.length > 0 && (
          <div className="bg-white border border-slate-200 p-6 mb-6">
            <h2 
              className="text-xl font-semibold text-slate-900 mb-3"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {t('repairmanProfile.servicesTitle')}
            </h2>
            <div className="flex flex-wrap gap-2">
              {repairman.services.map((service) => (
                <Badge key={service} className="bg-slate-100 text-slate-700">
                  {SERVICE_NAMES_MAP[service] ? t(SERVICE_NAMES_MAP[service]) : service}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Availability Calendar */}
        <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="calendar-section">
          <h2 
            className="text-xl font-semibold text-slate-900 mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <Calendar className="w-5 h-5 inline-block mr-2" />
            {t('calendar.availabilityTitle')}
          </h2>
          <ClientCalendar 
            repairmanId={repairmanId} 
            repairmanName={repairman.full_name}
            currentUser={currentUser}
          />
        </div>

        {/* Reviews */}
        <div id="reviews" className="bg-white border border-slate-200 p-6 scroll-mt-24">
          <h2 
            className="text-xl font-semibold text-slate-900 mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            <MessageSquare className="w-5 h-5 inline-block mr-2" />
            {t('repairmanProfile.reviewsTitle')} ({reviews.length})
          </h2>
          
          {reviews.length === 0 ? (
            <p className="text-slate-500 text-center py-8">{t('repairmanProfile.noReviews')}</p>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-slate-100 pb-4 last:border-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-slate-100 flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{review.customer_name}</p>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-4 h-4 ${
                              star <= review.rating
                                ? 'text-orange-500 fill-orange-500'
                                : 'text-slate-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-slate-600 ml-13">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default RepairmanProfile;
